import { useState, useEffect } from 'react';
import {
  AddressBookApiElementContentModel,
  AddressBookDto,
  AddressBookScreenContentModel,
  AddressBookScreenPropertiesModel,
} from '@/services/platform/api/model';
import { cn, ModuleProperties, useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores';
import ModuleWrapper from '@/components/shared/module/wrapper';
import {
  createAddress,
  deleteAddress,
  getAddresses,
  updateAddress,
} from '@/services/platform/data/addresses';
import { ProfileAccountAddressBook } from '@/components/shared/profile/account/address-book';

export const AddressBookApi = (props: ModuleProperties) => {
  const content = props.content as AddressBookApiElementContentModel | undefined;
  const [addresses, setAddresses] = useState<AddressBookDto[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const locale = useLocaleStore(state => state.locale);
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  // Figma has this screen running full-bleed to the bottom edge — the floating nav pill
  // would otherwise sit over the "add new address" CTA and intercept its taps.
  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  useEffect(() => {
    setLoading(true);
    getAddresses(locale)
      .then(data => setAddresses(data))
      .catch(() => setAddresses(undefined))
      .finally(() => setLoading(false));
  }, [locale, refetchTrigger]);

  const page = props.context?.page as AddressBookScreenContentModel;
  const pageProperties = page?.properties as AddressBookScreenPropertiesModel;

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__address-book-api']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ProfileAccountAddressBook
        addresses={addresses}
        pageProperties={pageProperties}
        loading={loading}
        onSave={async data => {
          await createAddress(data, locale);
          setRefetchTrigger(t => t + 1);
        }}
        onUpdate={async (id, data) => {
          await updateAddress(id, data, locale);
          setRefetchTrigger(t => t + 1);
        }}
        onDelete={async id => {
          const success = await deleteAddress(id);
          if (!success) throw new Error('Failed to delete address');
          setRefetchTrigger(t => t + 1);
        }}
      />
    </ModuleWrapper>
  );
};
